import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, MapPin, Upload, User } from "lucide-react";
import { Layout } from "../layout/Layout";
import { SuccessToast } from "../components/SuccessToast";
import {
	CircleMarker,
	MapContainer,
	TileLayer,
	useMap,
	useMapEvents,
} from "react-leaflet";
import type { LatLngLiteral } from "leaflet";
import { registerClient } from "../core/registration/registration.ts";
import {
	composePhoneWithCountryCode,
	getPhoneCountryCodeOptions,
	getPhoneValidationError,
	splitPhoneInputValue,
	splitPhoneWithCountryCode,
} from "../core/utils/phone";
import type {
	FormState,
	FieldErrors,
	PickedLocation,
	AddressSuggestion,
} from "../core/types/tsx/pages/RegistrationClient.types";

function MapClickHandler({ onPick }: { onPick: (coords: PickedLocation) => void }) {
	useMapEvents({
		click(e) {
			onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
		},
	});
	return null;
}

function MapFlyTo({ center, zoom }: { center: LatLngLiteral; zoom: number }) {
	const map = useMap();
	useEffect(() => {
		map.flyTo(center, zoom, { duration: 1.2 });
	}, [map, center, zoom]);
	return null;
}

async function reverseGeocodeNominatim(
	coords: PickedLocation,
	signal?: AbortSignal,
): Promise<string | null> {
	const { lat, lng } = coords;
	const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
		lat,
	)}&lon=${encodeURIComponent(lng)}`;
	const res = await fetch(url, {
		signal,
		headers: {
			Accept: "application/json",
			"Accept-Language": "es",
		},
	});
	if (!res.ok) return null;
	const data = (await res.json()) as { display_name?: string };
	const displayName =
		typeof data.display_name === "string" ? data.display_name.trim() : "";
	return displayName || null;
}

async function searchAddressNominatim(
	query: string,
	signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
	const q = query.trim();
	if (!q) return [];
	const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(q)}`;
	const res = await fetch(url, {
		signal,
		headers: {
			Accept: "application/json",
			"Accept-Language": "es",
		},
	});
	if (!res.ok) return [];
	const data = (await res.json()) as Array<{
		display_name?: string;
		lat?: string;
		lon?: string;
	}>;
	if (!Array.isArray(data)) return [];
	return data
		.map((item) => {
			const label =
				typeof item.display_name === "string" ? item.display_name.trim() : "";
			const lat = Number(item.lat);
			const lng = Number(item.lon);
			if (!label || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
			return { label, lat, lng };
		})
		.filter((x): x is AddressSuggestion => x !== null);
}


export function RegistrationClient() {
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [serverError, setServerError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);
	const [toastOpen, setToastOpen] = useState(false);
	const [errors, setErrors] = useState<FieldErrors>({});
	const [pickedLocation, setPickedLocation] = useState<PickedLocation | null>(null);
	const [reverseGeocodeStatus, setReverseGeocodeStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const reverseGeocodeAbortRef = useRef<AbortController | null>(null);

	// Reemplazo de mapCenter y mapZoom por flyTarget
	const [flyTarget, setFlyTarget] = useState<{ center: LatLngLiteral; zoom: number }>({
		center: { lat: 10.4806, lng: -66.9036 },
		zoom: 9,
	});
	const [geoStatus, setGeoStatus] = useState<"loading" | "done" | "error">("loading");
	const hasGeolocatedRef = useRef(false);

	const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
	const [addressSuggestStatus, setAddressSuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const addressSuggestAbortRef = useRef<AbortController | null>(null);
	const addressSuggestDebounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(
		null,
	);

	useEffect(() => {
		if (hasGeolocatedRef.current) return;
		hasGeolocatedRef.current = true;

		if (!navigator.geolocation) {
			setGeoStatus("error");
			return;
		}

		setGeoStatus("loading");
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const lat = pos.coords.latitude;
				const lng = pos.coords.longitude;
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
					setGeoStatus("error");
					return;
				}
				setFlyTarget({ center: { lat, lng }, zoom: 16 });
				setGeoStatus("done");
			},
			(err) => {
				if (err && err.code === 1) {
					// PERMISSION_DENIED
					setGeoStatus("error");
				} else {
					setGeoStatus("loading");
				}
			},
			{ enableHighAccuracy: false },
		);
	}, []);

	useEffect(() => {
		return () => {
			reverseGeocodeAbortRef.current?.abort();
			addressSuggestAbortRef.current?.abort();
			if (addressSuggestDebounceRef.current)
				window.clearTimeout(addressSuggestDebounceRef.current);
		};
	}, []);

	const [form, setForm] = useState<FormState>({
		nombre: "",
		apellido: "",
		email: "",
		telefono: "",
		password: "",
		confirmPassword: "",
		direccion: "",
		accepted: false,
	});

	const inputBase =
		"w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary";
	const inputWithIcon =
		"w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary";

	const photoLabel = useMemo(() => {
		if (!form.photoFile) return "Subir Foto";
		return "Cambiar Foto";
	}, [form.photoFile]);

	const phoneParts = useMemo(
		() => splitPhoneWithCountryCode(form.telefono),
		[form.telefono],
	);

	const phoneCountryCodeOptions = useMemo(
		() => getPhoneCountryCodeOptions(phoneParts.countryCode),
		[phoneParts.countryCode],
	);

	const errorText = "mt-1 text-xs text-red-600";
	const clearError = (key: keyof FormState) => {
		setErrors((prev) => {
			if (!prev[key]) return prev;
			const { [key]: _omit, ...rest } = prev;
			void _omit;
			return rest;
		});
	};

	const validate = (): boolean => {
		const next: FieldErrors = {};
		const nombre = form.nombre.trim();
		const apellido = form.apellido.trim();
		const email = form.email.trim();
		const telefono = form.telefono.trim();
		const direccion = form.direccion.trim();
		const password = form.password.trim();
		const confirmPassword = form.confirmPassword.trim();

		if (!nombre) next.nombre = "Este campo es obligatorio";
		else if (nombre.length < 2) next.nombre = "Debe tener al menos 2 caracteres";
		else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(nombre))
			next.nombre = "Solo se permiten letras en el nombre";
		if (!apellido) next.apellido = "Este campo es obligatorio";
		else if (apellido.length < 2) next.apellido = "Debe tener al menos 2 caracteres";
		else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(apellido))
			next.apellido = "Solo se permiten letras en el apellido";
		if (!email) next.email = "Este campo es obligatorio";
		else if (!/^\S+@\S+\.\S+$/.test(email)) next.email = "Correo inválido";
		const phoneError = getPhoneValidationError(telefono, { required: true });
		if (phoneError) next.telefono = phoneError;
		if (!password) next.password = "Este campo es obligatorio";
		else if (password.length < 8) next.password = "Debe tener al menos 8 caracteres";
		else if (!/\d/.test(password)) next.password = "Debe incluir al menos un número";
		if (!confirmPassword) next.confirmPassword = "Este campo es obligatorio";
		if (password && confirmPassword && password !== confirmPassword)
			next.confirmPassword = "Las contraseñas no coinciden";
		if (!direccion) next.direccion = "Este campo es obligatorio";
		else if (direccion.length < 6)
			next.direccion = "Describe una dirección más precisa";
		if (!form.accepted) next.accepted = "Debes aceptar las políticas y términos";
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setServerError(null);
		setSuccess(false);
		setToastOpen(false);
		if (!validate()) return;
		setSubmitting(true);
		try {
			await registerClient({
				names: form.nombre.trim(),
				lastNames: form.apellido.trim(),
				password: form.password,
				email: form.email.trim(),
				phone: form.telefono.trim(),
				address: form.direccion.trim(),
				photoFile: form.photoFile,
			});

			setSuccess(true);
			setToastOpen(true);
			setTimeout(() => navigate("/login"), 2500);
		} catch (err) {
			setServerError(
				err instanceof Error ? err.message : "No se pudo completar el registro",
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Layout>
			<SuccessToast
				open={toastOpen}
				title='Nuevo usuario'
				message={`Felicidades, se ha registrado exitosamente ${form.nombre.trim()}${form.apellido.trim() ? ` ${form.apellido.trim()}` : ""}`}
				onClose={() => setToastOpen(false)}
			/>
			<div className='bg-white px-4 py-6'>
				<div className='mx-auto w-full max-w-6xl'>
					<div className='mb-4'>
						<Link
							to='/registro'
							className='text-base font-medium text-slate-700 hover:text-slate-900'
						>
							&lt; Datos Personales
						</Link>
						<p className='mt-1 text-sm text-slate-500'>
							Completa tu información personal
						</p>
					</div>

					<form onSubmit={onSubmit} className='space-y-6'>
						{serverError ? (
							<div className='rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
								{serverError}
							</div>
						) : null}
						{success ? (
							<div className='rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700'>
								Registro completado.
							</div>
						) : null}

						<div className='flex flex-wrap items-center gap-6'>
							<div className='flex items-center gap-4'>
								<div className='flex h-20 w-20 items-center justify-center rounded-full bg-slate-200 text-slate-500 overflow-hidden'>
									{form.photoFile ? (
										<img
											src={URL.createObjectURL(form.photoFile)}
											alt='Foto de perfil'
											className='h-20 w-20 object-cover rounded-full'
										/>
									) : (
										<User className='h-8 w-8' />
									)}
								</div>
								<div>
									<label className='inline-flex cursor-pointer items-center gap-2 rounded-lg border border-primary bg-white px-4 py-2 text-sm font-medium text-primary hover:bg-secondary'>
										<Upload className='h-4 w-4' />
										{photoLabel}
										<input
											type='file'
											accept='image/png,image/jpeg'
											className='hidden'
											onChange={(e) => {
												const file = e.target.files?.[0];
												setForm((f) => ({
													...f,
													photoFile: file,
												}));
											}}
										/>
									</label>
									<p className='mt-2 text-xs text-slate-400'>
										Opcional - JPG, PNG (máx. 5MB)
									</p>
									{form.photoFile ? (
										<span className='text-xs text-slate-500'>
											Previsualización
										</span>
									) : null}
								</div>
							</div>
						</div>

						<div className='grid gap-4 md:grid-cols-2'>
							<div className='space-y-2'>
								<label className='block text-xs font-medium text-slate-600'>
									Nombre *
								</label>
								<input
									type='text'
									value={form.nombre}
									onChange={(e) => {
										clearError("nombre");
										setForm((f) => ({
											...f,
											nombre: e.target.value,
										}));
									}}
									placeholder='Juan'
									autoComplete='given-name'
									className={inputBase}
								/>
								{errors.nombre ? (
									<p className={errorText}>{errors.nombre}</p>
								) : null}
							</div>
							<div className='space-y-2'>
								<label className='block text-xs font-medium text-slate-600'>
									Apellido *
								</label>
								<input
									type='text'
									value={form.apellido}
									onChange={(e) => {
										clearError("apellido");
										setForm((f) => ({
											...f,
											apellido: e.target.value,
										}));
									}}
									placeholder='Pérez'
									autoComplete='family-name'
									className={inputBase}
								/>
								{errors.apellido ? (
									<p className={errorText}>{errors.apellido}</p>
								) : null}
							</div>

							<div className='space-y-2'>
								<label
									htmlFor='register-client-email'
									className='block text-xs font-medium text-slate-600'
								>
									Correo Electrónico *
								</label>
								<div className='relative'>
									<Mail className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
									<input
										id='register-client-email'
										name='email'
										type='email'
										value={form.email}
										onChange={(e) => {
											clearError("email");
											setForm((f) => ({
												...f,
												email: e.target.value,
											}));
										}}
										placeholder='juan.perez@email.com'
										autoComplete='email'
										className={inputWithIcon}
									/>
								</div>
								{errors.email ? (
									<p className={errorText}>{errors.email}</p>
								) : null}
							</div>

							<div className='space-y-2'>
								<label className='block text-xs font-medium text-slate-600'>
									Teléfono *
								</label>
								<div className='grid grid-cols-[140px_1fr] gap-2 sm:grid-cols-[180px_1fr]'>
									<select
										value={phoneParts.countryCode}
										onChange={(e) => {
											clearError("telefono");
											setForm((f) => ({
												...f,
												telefono: composePhoneWithCountryCode(
													e.target.value,
													phoneParts.localNumber,
												),
											}));
										}}
										className={inputBase}
									>
										{phoneCountryCodeOptions.map((option) => (
											<option
												key={option.value}
												value={option.value}
											>
												{option.label}
											</option>
										))}
									</select>
									<input
										type='tel'
										inputMode='tel'
										value={phoneParts.localNumber}
										onChange={(e) => {
											const nextPhoneParts = splitPhoneInputValue(
												e.target.value,
												phoneParts.countryCode,
												{
													inferCallingCodeFromPlainDigits: false,
													codeOnlyMeansEmptyLocal: false,
												},
											);
											clearError("telefono");
											setForm((f) => ({
												...f,
												telefono: composePhoneWithCountryCode(
													nextPhoneParts.countryCode,
													nextPhoneParts.localNumber,
												),
											}));
										}}
										placeholder='412-1234567'
										autoComplete='tel'
										className={inputBase}
									/>
								</div>
								{errors.telefono ? (
									<p className={errorText}>{errors.telefono}</p>
								) : null}
							</div>


							<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-2'>
								<div className='space-y-2'>
									<label
										htmlFor='register-client-password'
										className='block text-xs font-medium text-slate-600'
									>
										Contraseña *
									</label>
									<div className='relative'>
										<input
											id='register-client-password'
											name='password'
											type={showPassword ? "text" : "password"}
											value={form.password}
											onChange={(e) => {
												clearError("password");
												setForm((f) => ({
													...f,
													password: e.target.value,
												}));
											}}
											placeholder='••••••••'
											autoComplete='new-password'
											className={inputBase + " pr-10"}
										/>
										<button
											type='button'
											aria-label={
												showPassword
													? "Ocultar contraseña"
													: "Mostrar contraseña"
											}
											onClick={() => setShowPassword((v) => !v)}
											className='cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
										>
											{showPassword ? (
												<EyeOff className='h-4 w-4' />
											) : (
												<Eye className='h-4 w-4' />
											)}
										</button>
									</div>
									{errors.password ? (
										<p className={errorText}>{errors.password}</p>
									) : null}
								</div>

								<div className='space-y-2'>
									<label
										htmlFor='register-client-confirm-password'
										className='block text-xs font-medium text-slate-600'
									>
										Confirmar Contraseña *
									</label>
									<div className='relative'>
										<input
											id='register-client-confirm-password'
											name='confirmPassword'
											type={showPassword ? "text" : "password"}
											value={form.confirmPassword}
											onChange={(e) => {
												clearError("confirmPassword");
												setForm((f) => ({
													...f,
													confirmPassword: e.target.value,
												}));
											}}
											placeholder='••••••••'
											autoComplete='new-password'
											className={inputBase + " pr-10"}
										/>
										<button
											type='button'
											aria-label={
												showPassword
													? "Ocultar contraseña"
													: "Mostrar contraseña"
											}
											onClick={() => setShowPassword((v) => !v)}
											className='cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
										>
											{showPassword ? (
												<EyeOff className='h-4 w-4' />
											) : (
												<Eye className='h-4 w-4' />
											)}
										</button>
									</div>
									{errors.confirmPassword ? (
										<p className={errorText}>
											{errors.confirmPassword}
										</p>
									) : null}
								</div>
							</div>

							<div className='space-y-2 md:col-span-2'>
								<label className='block text-xs font-medium text-slate-600'>
									Dirección *
								</label>
								<div className='relative'>
									<MapPin className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
									<input
										type='text'
										value={form.direccion}
										onChange={(e) => {
											const nextValue = e.target.value;
											clearError("direccion");
											setForm((f) => ({
												...f,
												direccion: nextValue,
											}));

											addressSuggestAbortRef.current?.abort();
											if (addressSuggestDebounceRef.current) {
												window.clearTimeout(
													addressSuggestDebounceRef.current,
												);
												addressSuggestDebounceRef.current = null;
											}
											setAddressSuggestStatus("idle");
											const q = nextValue.trim();
											if (q.length < 3) {
												setAddressSuggestions([]);
												return;
											}

											addressSuggestDebounceRef.current =
												window.setTimeout(async () => {
													setAddressSuggestStatus("loading");
													const controller =
														new AbortController();
													addressSuggestAbortRef.current =
														controller;
													try {
														const results =
															await searchAddressNominatim(
																q,
																controller.signal,
															);
														setAddressSuggestions(results);
														setAddressSuggestStatus("idle");
													} catch (err) {
														if (
															err instanceof DOMException &&
															err.name === "AbortError"
														)
															return;
														setAddressSuggestions([]);
														setAddressSuggestStatus("error");
													}
												}, 350);
										}}
										onBlur={() => {
											window.setTimeout(
												() => setAddressSuggestions([]),
												150,
											);
										}}
										onKeyDown={(e) => {
											if (e.key === "Escape")
												setAddressSuggestions([]);
										}}
										placeholder='Ej: Caracas, Chacao'
										className={inputWithIcon}
									/>
									{addressSuggestions.length > 0 ? (
										<ul className='absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white'>
											{addressSuggestions.map((sug) => (
												<li
													key={`${sug.lat},${sug.lng},${sug.label}`}
													className='border-b border-slate-100 last:border-b-0'
												>
													<button
														type='button'
														className='cursor-pointer w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
														onMouseDown={(evt) => {
															evt.preventDefault();
															clearError("direccion");
															addressSuggestAbortRef.current?.abort();
															setForm((f) => ({
																...f,
																direccion: sug.label,
															}));
															setPickedLocation({
																lat: sug.lat,
																lng: sug.lng,
															});
															setFlyTarget({
																center: {
																	lat: sug.lat,
																	lng: sug.lng,
																},
																zoom: 16,
															});
															setAddressSuggestions([]);
															setAddressSuggestStatus(
																"idle",
															);
														}}
													>
														{sug.label}
													</button>
												</li>
											))}
										</ul>
									) : null}
								</div>
								{addressSuggestStatus === "loading" ? (
									<p className='mt-1 text-xs text-slate-500'>
										Buscando sugerencias...
									</p>
								) : null}
								{errors.direccion ? (
									<p className={errorText}>{errors.direccion}</p>
								) : null}

								<div className='mt-3 rounded-xl border border-slate-200 bg-white p-3'>
									<p className='text-xs text-slate-500'>
										Selecciona un punto en el mapa para autocompletar
										la dirección.
									</p>

									{geoStatus === "loading" ? (
										<div className='mt-2 flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2 text-sm text-slate-500'>
											<span className='inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent' />
											Obteniendo tu ubicación...
										</div>
									) : geoStatus === "error" ? (
										<div className='mt-2 rounded-xl bg-red-50 px-4 py-2 text-sm text-red-600'>
											No se pudo obtener tu ubicación. Puedes
											seleccionar manualmente en el mapa.
										</div>
									) : null}

									<div className='mt-3 h-64 overflow-hidden rounded-lg border border-slate-200 bg-slate-200'>
										<MapContainer
											center={flyTarget.center}
											zoom={flyTarget.zoom}
											scrollWheelZoom
											className='h-full w-full'
										>
											<TileLayer
												attribution='&copy; OpenStreetMap contributors'
												url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
											/>
											<MapFlyTo
												center={flyTarget.center}
												zoom={flyTarget.zoom}
											/>
											<MapClickHandler
												onPick={async (coords) => {
													clearError("direccion");
													setPickedLocation(coords);
													setAddressSuggestions([]);
													setReverseGeocodeStatus("loading");
													reverseGeocodeAbortRef.current?.abort();
													const controller =
														new AbortController();
													reverseGeocodeAbortRef.current =
														controller;
													try {
														const address =
															await reverseGeocodeNominatim(
																coords,
																controller.signal,
															);
														setForm((f) => ({
															...f,
															direccion:
																address ??
																`${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
														}));
														setReverseGeocodeStatus(
															address ? "idle" : "error",
														);
													} catch (err) {
														if (
															err instanceof DOMException &&
															err.name === "AbortError"
														)
															return;
														setForm((f) => ({
															...f,
															direccion: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
														}));
														setReverseGeocodeStatus("error");
													}
												}}
											/>
											{pickedLocation ? (
												<CircleMarker
													center={{
														lat: pickedLocation.lat,
														lng: pickedLocation.lng,
													}}
													radius={8}
												/>
											) : null}
										</MapContainer>
									</div>

									{reverseGeocodeStatus === "loading" ? (
										<p className='mt-2 text-xs text-slate-500'>
											Buscando dirección...
										</p>
									) : reverseGeocodeStatus === "error" ? (
										<p className='mt-2 text-xs text-slate-500'>
											No se pudo obtener una dirección exacta. Se
											guardaron coordenadas.
										</p>
									) : null}
									{pickedLocation ? (
										<p className='mt-1 text-xs text-slate-500'>
											Ubicación seleccionada:{" "}
											{pickedLocation.lat.toFixed(6)},{" "}
											{pickedLocation.lng.toFixed(6)}
										</p>
									) : null}
								</div>
							</div>
						</div>

						<label className='flex items-center gap-2 text-sm text-slate-600'>
							<input
								type='checkbox'
								checked={form.accepted}
								onChange={(e) => {
									clearError("accepted");
									setForm((f) => ({
										...f,
										accepted: e.target.checked,
									}));
								}}
								className='h-4 w-4 rounded border-slate-300 text-primary focus:ring-secondary'
							/>
							<span>*Acepto las políticas y términos</span>
						</label>
						{errors.accepted ? (
							<p className={errorText}>{errors.accepted}</p>
						) : null}

						<button
							type='submit'
							disabled={submitting}
							className={
								"cursor-pointer w-full rounded-full bg-primary py-3 text-sm font-semibold text-white transition hover:bg-primary-hover active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
							}
						>
							{submitting ? "Registrando..." : "Registrar"}
						</button>
					</form>
				</div>
			</div>
		</Layout>
	);
}
